import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { FaComment, FaTimes, FaPaperPlane, FaCheckCircle, FaPlus, FaBan, FaTrash, FaMicrophone, FaStop, FaVolumeUp } from 'react-icons/fa';
import { sendChatMessage, discardChatModification } from '../api/client';
import { VoiceInputInline } from './voice/VoiceInput';
import { TextToSpeechInline } from './voice/TextToSpeech';
import { useVoiceSupport } from './voice/VoiceProvider';
import './Chatbot.css';

// Format chat message text into structured HTML with bullet lists and bold
const formatMessageText = (text) => {
  if (!text) return '';
  const lines = text.split('\n');
  let html = '';
  let inList = false;
  let listType = null; // 'ul' or 'ol'

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const ulMatch = line.match(/^\s*[-*•]\s+(.*)/);
    const olMatch = line.match(/^\s*(\d+)[.)]\s+(.*)/);

    if (ulMatch) {
      if (!inList || listType !== 'ul') {
        if (inList) html += listType === 'ol' ? '</ol>' : '</ul>';
        html += '<ul>';
        inList = true;
        listType = 'ul';
      }
      html += `<li>${ulMatch[1]}</li>`;
    } else if (olMatch) {
      if (!inList || listType !== 'ol') {
        if (inList) html += listType === 'ol' ? '</ol>' : '</ul>';
        html += '<ol>';
        inList = true;
        listType = 'ol';
      }
      html += `<li>${olMatch[2]}</li>`;
    } else {
      if (inList) {
        html += listType === 'ol' ? '</ol>' : '</ul>';
        inList = false;
        listType = null;
      }
      if (line.trim() === '') {
        html += '<br/>';
      } else {
        html += `<p>${line}</p>`;
      }
    }
  }
  if (inList) html += listType === 'ol' ? '</ol>' : '</ul>';

  // Bold: **text**
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  return DOMPurify.sanitize(html);
};

// Sub-component for modification selection with checkboxes
const ModificationSelector = ({ modifications, onConfirm, onSkipAll }) => {
  const [selected, setSelected] = useState(modifications.map(() => true));
  const toggleItem = (i) => setSelected((prev) => prev.map((v, j) => j === i ? !v : v));
  const selectedCount = selected.filter(Boolean).length;

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', marginTop: '10px' }}>
      <p style={{ fontSize: '12px', color: '#475569', margin: '0 0 10px', fontWeight: 600 }}>
        {modifications.length > 1 ? 'Select stops to add to your itinerary:' : 'Add this stop to your itinerary?'}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
        {modifications.map((mod, i) => (
          <label
            key={mod.id}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
              background: selected[i] ? '#dcfce7' : '#ffffff',
              border: `2px solid ${selected[i] ? '#22c55e' : '#d1d5db'}`,
              borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s',
              fontSize: '13px', lineHeight: '1.4',
              boxShadow: selected[i] ? '0 2px 4px rgba(34, 197, 94, 0.15)' : 'none'
            }}
          >
            <input
              type="checkbox"
              checked={selected[i]}
              onChange={() => toggleItem(i)}
              style={{
                accentColor: '#22c55e',
                width: '20px',
                height: '20px',
                minWidth: '20px',
                minHeight: '20px',
                flexShrink: 0,
                cursor: 'pointer',
                appearance: 'auto',
                WebkitAppearance: 'checkbox',
                border: '2px solid #22c55e',
                borderRadius: '4px'
              }}
            />
            <span style={{ color: selected[i] ? '#166534' : '#6b7280', fontWeight: selected[i] ? 500 : 400 }}>
              {mod.description}
            </span>
          </label>
        ))}
      </div>
      <div className="chatbot-action-buttons">
        <button
          className="chatbot-apply-button"
          onClick={() => onConfirm(selected)}
          disabled={selectedCount === 0}
          style={{ opacity: selectedCount === 0 ? 0.5 : 1 }}
        >
          <FaPlus style={{ fontSize: '10px' }} /> Add {selectedCount > 1 ? `${selectedCount} Stops` : selectedCount === 1 ? 'to Itinerary' : 'None'}
        </button>
        <button className="chatbot-cancel-button" onClick={onSkipAll}>
          Skip
        </button>
      </div>
    </div>
  );
};

const Chatbot = ({ requestId, onModificationApplied }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const dragRef = useRef(null);
  const messagesEndRef = useRef(null);
  const voiceSupport = useVoiceSupport();

  // Handle voice transcript - append to input
  const handleVoiceTranscript = useCallback((transcript) => {
    if (transcript) {
      setInputValue(transcript);
    }
  }, []);

  // Handle final voice transcript - optionally auto-send
  const handleFinalVoiceTranscript = useCallback((transcript) => {
    if (transcript && transcript.trim()) {
      setInputValue(transcript.trim());
    }
  }, []);

  // Extract plain text from AI message for TTS
  const getPlainTextFromMessage = (content) => {
    if (!content) return '';
    // Remove markdown formatting
    let text = content
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/^\s*[-*•]\s+/gm, '')
      .replace(/^\s*\d+[.)]\s+/gm, '')
      .replace(/\n+/g, ' ')
      .trim();
    return text;
  };

  // Draggable window
  const handleDragStart = (e) => {
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
    const rect = dragRef.current.getBoundingClientRect();
    const startX = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const startY = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    const handleMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const newX = cx - startX;
      const newY = cy - startY;
      const maxX = window.innerWidth - dragRef.current.offsetWidth;
      const maxY = window.innerHeight - dragRef.current.offsetHeight;
      dragRef.current.style.left = `${Math.max(0, Math.min(newX, maxX))}px`;
      dragRef.current.style.top = `${Math.max(0, Math.min(newY, maxY))}px`;
      dragRef.current.style.right = 'auto';
      dragRef.current.style.bottom = 'auto';
      setIsDragging(true);
    };
    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      setTimeout(() => setIsDragging(false), 0);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  // Show tooltip for first-time users
  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem('artie_tooltip_seen');
    if (!hasSeenTooltip) {
      setShowTooltip(true);
    }
  }, []);

  const dismissTooltip = () => {
    setShowTooltip(false);
    localStorage.setItem('artie_tooltip_seen', 'true');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');

    // Add user message to chat
    setMessages((prev) => [...prev, { type: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const data = await sendChatMessage(requestId, userMessage);

      const { response: aiResponse, modification_description, modification_id, modifications } = data;

      // Debug logging
      console.log('Artie response data:', { aiResponse, modification_description, modification_id, modifications });

      // Determine if there's a valid modification to show
      const hasValidModification = modification_description &&
        modification_description.trim() !== '' &&
        modification_description.toLowerCase() !== 'no modification needed' &&
        modification_description.toLowerCase() !== 'none' &&
        !modification_description.toLowerCase().includes('no modification');

      // Add AI response to chat
      setMessages((prev) => [
        ...prev,
        {
          type: 'ai',
          content: aiResponse,
          modificationDescription: modification_description,
          modificationId: modification_id,
          // If multiple modifications, store them for checkbox UI
          modifications: modifications || (
            hasValidModification
              ? [{ id: modification_id, description: modification_description }]
              : null
          ),
        },
      ]);
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setMessages((prev) => [
        ...prev,
        {
          type: 'error',
          content: error.response?.data?.detail || error.message || 'Sorry, I encountered an error. Please try again.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSelections = async (messageIndex, modifications, selected) => {
    const selectedIds = modifications.filter((_, i) => selected[i]).map((m) => m.id);
    const skippedIds = modifications.filter((_, i) => !selected[i]).map((m) => m.id);

    // Discard unselected
    for (const id of skippedIds) {
      try { await discardChatModification(requestId, id); } catch (e) { /* ignore */ }
    }

    // Mark message as confirmed
    setMessages((prev) => prev.map((m, i) =>
      i === messageIndex ? { ...m, changeStatus: selectedIds.length > 0 ? 'accepted' : 'skipped', selectedCount: selectedIds.length } : m
    ));

    if (selectedIds.length > 0 && onModificationApplied) onModificationApplied('queued');
  };

  const handleSkipAll = async (messageIndex, modifications) => {
    for (const mod of modifications) {
      try { await discardChatModification(requestId, mod.id); } catch (e) { /* ignore */ }
    }
    setMessages((prev) => prev.map((m, i) =>
      i === messageIndex ? { ...m, changeStatus: 'skipped' } : m
    ));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chatbot Toggle Button with Tooltip */}
      {!isOpen && (
        <div className="chatbot-toggle-container">
          {showTooltip && (
            <div className="chatbot-tooltip">
              <div className="chatbot-tooltip-content">
                <div className="chatbot-tooltip-header">
                  <span className="chatbot-tooltip-icon">&#128075;</span>
                  <strong>Meet Artie!</strong>
                </div>
                <p>Your AI travel assistant is here to help! Ask Artie to:</p>
                <ul>
                  <li>Add attractions, restaurants, or activities</li>
                  <li>Adjust timing or change schedules</li>
                  <li>Get local tips and recommendations</li>
                  <li>Modify your itinerary on the fly</li>
                </ul>
                <button className="chatbot-tooltip-dismiss" onClick={dismissTooltip}>
                  Got it!
                </button>
              </div>
              <div className="chatbot-tooltip-arrow"></div>
            </div>
          )}
          <button
            className={`chatbot-toggle-button ${showTooltip ? 'chatbot-toggle-highlight' : ''}`}
            onClick={() => { setIsOpen(true); dismissTooltip(); }}
            title="Chat with Artie - Your AI Travel Assistant"
          >
            <FaComment />
            <span className="chatbot-badge">Artie</span>
          </button>
        </div>
      )}

      {/* Chatbot Window */}
      {isOpen && (
        <div className="chatbot-window" ref={dragRef}>
          {/* Header - drag handle */}
          <div className="chatbot-header" onMouseDown={handleDragStart} onTouchStart={handleDragStart} style={{ cursor: 'grab' }}>
            <div className="chatbot-header-title">
              <span className="chatbot-artie-icon">👨‍✈️</span>
              <h3>Artie</h3>
              <span className="chatbot-subtitle">AI Travel Assistant</span>
            </div>
            <div className="chatbot-header-actions">
              {messages.length > 0 && (
                <button
                  className="chatbot-clear-button"
                  onClick={(e) => { e.stopPropagation(); setMessages([]); }}
                  title="Clear chat"
                >
                  <FaTrash />
                </button>
              )}
              <button className="chatbot-close-button" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}>
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.length === 0 && (
              <div className="chatbot-welcome">
                <div className="chatbot-welcome-header">
                  <span className="chatbot-welcome-icon">👨‍✈️</span>
                  <p>Hi! I'm <strong>Artie</strong>, your AI travel assistant. I can help customize your itinerary - just ask!</p>
                </div>
                <div className="chatbot-suggestions">
                  <button onClick={() => setInputValue('Can you add a museum to my itinerary?')}>
                    Add a museum
                  </button>
                  <button onClick={() => setInputValue('I want more vegetarian restaurant options')}>
                    More vegetarian options
                  </button>
                  <button onClick={() => setInputValue('Add some outdoor activities')}>
                    Add outdoor activities
                  </button>
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div key={index} className={`chatbot-message chatbot-message-${message.type}`}>
                <div className="chatbot-message-header">
                  <div
                    className="chatbot-message-content"
                    dangerouslySetInnerHTML={{ __html: formatMessageText(message.content) }}
                  />
                  {message.type === 'ai' && voiceSupport.speechSynthesis && (
                    <TextToSpeechInline
                      text={getPlainTextFromMessage(message.content)}
                      className="chatbot-tts-button"
                    />
                  )}
                </div>

                {/* Show upgrade button for subscription messages */}
                {message.type === 'subscription' && (
                  <div className="chatbot-subscription-cta">
                    <a
                      href="/pricing"
                      className="chatbot-upgrade-button"
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/#pricing';
                      }}
                    >
                      View Plans & Upgrade
                    </a>
                  </div>
                )}

                {/* Show selection UI for modifications */}
                {message.type === 'ai' && message.modifications && message.modifications.length > 0 && (
                  <div className="chatbot-modification-actions">
                    {!message.changeStatus && (
                      <ModificationSelector
                        modifications={message.modifications}
                        onConfirm={(selected) => handleConfirmSelections(index, message.modifications, selected)}
                        onSkipAll={() => handleSkipAll(index, message.modifications)}
                      />
                    )}
                    {message.changeStatus === 'accepted' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', color: '#059669', fontSize: '12px', fontWeight: 600 }}>
                        <FaCheckCircle /> {message.selectedCount || message.modifications.length} change{(message.selectedCount || message.modifications.length) > 1 ? 's' : ''} added to pending — regenerate when ready
                      </div>
                    )}
                    {message.changeStatus === 'skipped' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', color: '#9ca3af', fontSize: '12px' }}>
                        <FaBan /> Skipped
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="chatbot-message chatbot-message-ai">
                <div className="chatbot-typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chatbot-input-container">
            <textarea
              className="chatbot-input"
              placeholder={isListening ? "Listening..." : "Ask about your trip..."}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              rows="2"
              disabled={isLoading}
            />
            <div className="chatbot-input-buttons">
              {voiceSupport.speechRecognition && (
                <VoiceInputInline
                  onTranscript={handleVoiceTranscript}
                  onListeningChange={setIsListening}
                  disabled={isLoading}
                  className="chatbot-voice-button"
                />
              )}
              <button
                className="chatbot-send-button"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
